import { useRef, useState } from 'react'
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa'
import { Select } from '../../../styles/global'
import { Modal } from './Modal'
import { ConductorFields } from './ConductorFields'
import { createDefaultConfig, getTerminalColumnCount } from './conductorConfig'
import { ActionButton, Checkbox, FieldRow, Hint, InlineCheck, Table, TableContainer, Td, Th, Tr } from './styles'

function isValidElement(quantity, electricPower) {
  return Number(quantity) > 0 && Number(electricPower) > 0
}

// Cria ou edita um circuito. Um circuito novo começa sem regra de demanda e sem IDR.
export function CircuitModal({
  circuit,
  boardName,
  demandRules,
  idrs,
  allowsTwoPoles,
  conductorOptions,
  groupingOptions,
  onSave,
  onClose,
}) {
  const [name, setName] = useState(circuit?.name || '')
  const [isLightCircuit, setIsLightCircuit] = useState(Boolean(circuit?.isLightCircuit))
  const [isTwoPoles, setIsTwoPoles] = useState(Boolean(circuit?.isTwoPoles))
  const [demandRuleId, setDemandRuleId] = useState(circuit?.demandRuleId || '')
  const [idrId, setIdrId] = useState(circuit?.idrId || '')
  const [config, setConfig] = useState(() =>
    circuit
      ? {
          conductor: circuit.conductor,
          grouping: circuit.grouping,
          ...(circuit.groupFactor !== undefined && { groupFactor: circuit.groupFactor }),
        }
      : createDefaultConfig()
  )
  const [elements, setElements] = useState(() => circuit?.elements || [])
  const [quantity, setQuantity] = useState('')
  const [electricPower, setElectricPower] = useState('')
  const quantityRef = useRef(null)
  const powerRef = useRef(null)

  const canAddElement = isValidElement(quantity, electricPower)
  const finalElements = canAddElement
    ? [...elements, { quantity: Number(quantity), electricPower: Number(electricPower) }]
    : elements
  const totalPower = finalElements.reduce((total, item) => total + item.quantity * item.electricPower, 0)

  function addElement() {
    if (!canAddElement) return

    setElements(finalElements)
    setQuantity('')
    setElectricPower('')
    quantityRef.current?.focus()
  }

  function handleQuantityKeyDown(event) {
    if (event.key !== 'Enter') return

    event.preventDefault()
    if (canAddElement) addElement()
    else powerRef.current?.focus()
  }

  function handlePowerKeyDown(event) {
    if (event.key !== 'Enter') return

    event.preventDefault()
    if (canAddElement) addElement()
    else if (!quantity) quantityRef.current?.focus()
  }

  function handleSave() {
    if (!name.trim()) return

    onSave({
      name: name.trim(),
      isLightCircuit,
      isTwoPoles: allowsTwoPoles && isTwoPoles,
      demandRuleId: demandRuleId || undefined,
      idrId: idrId || undefined,
      conductor: config.conductor,
      grouping: config.grouping,
      groupFactor: config.groupFactor,
      elements: finalElements,
    })
  }

  return (
    <Modal
      title={circuit ? `Editar circuito ${circuit.id} - ${boardName}` : `Novo circuito - ${boardName}`}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <ActionButton type="button" $variant="secondary" onClick={onClose}>
            Cancelar
          </ActionButton>
          <ActionButton type="button" disabled={!name.trim()} onClick={handleSave}>
            {circuit ? 'Salvar circuito' : 'Adicionar circuito'}
          </ActionButton>
        </>
      }
    >
      <FieldRow>
        <label>
          Nome
          <input
            type="text"
            value={name}
            placeholder="Ex.: Tomadas da cozinha"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
      </FieldRow>
      <FieldRow>
        <label>
          Regra de demanda
          <Select
            id="circuitDemandRule"
            value={demandRules.some((rule) => rule.id === demandRuleId) ? demandRuleId : ''}
            onChange={(event) => setDemandRuleId(event.target.value)}
          >
            <option value="">Sem regra (FD 100%)</option>
            {demandRules.map((rule) => (
              <option key={rule.id} value={rule.id}>
                {rule.name || rule.id}
              </option>
            ))}
          </Select>
        </label>
        <label>
          IDR
          <Select
            id="circuitIdr"
            value={idrs.some((idr) => idr.id === idrId) ? idrId : ''}
            onChange={(event) => setIdrId(event.target.value)}
          >
            <option value="">Sem IDR</option>
            {idrs.map((idr) => (
              <option key={idr.id} value={idr.id}>
                {idr.id} - {idr.name}
              </option>
            ))}
          </Select>
        </label>
      </FieldRow>
      <FieldRow>
        <InlineCheck>
          <Checkbox checked={isLightCircuit} onChange={(event) => setIsLightCircuit(event.target.checked)} />
          Circuito de iluminação
        </InlineCheck>
        {allowsTwoPoles && (
          <InlineCheck>
            <Checkbox checked={isTwoPoles} onChange={(event) => setIsTwoPoles(event.target.checked)} />
            Circuito fase-fase
          </InlineCheck>
        )}
      </FieldRow>

      <h3>Elementos do circuito</h3>
      <FieldRow>
        <label>
          Quantidade
          <input
            ref={quantityRef}
            type="number"
            min="0"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            onKeyDown={handleQuantityKeyDown}
          />
        </label>
        <label>
          Potência por unidade (VA)
          <input
            ref={powerRef}
            type="number"
            min="0"
            value={electricPower}
            onChange={(event) => setElectricPower(event.target.value)}
            onKeyDown={handlePowerKeyDown}
          />
        </label>
        <ActionButton type="button" disabled={!canAddElement} style={{ alignSelf: 'flex-end' }} onClick={addElement}>
          <FaPlusCircle style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Adicionar
        </ActionButton>
      </FieldRow>
      <Hint style={{ marginTop: 0 }}>Enter na potência adiciona o elemento e volta o foco para a quantidade.</Hint>
      {elements.length === 0 ? (
        <p>Nenhum elemento inserido</p>
      ) : (
        <TableContainer>
          <Table style={{ marginTop: '12px' }}>
            <thead>
              <Tr>
                <Th>Quantidade</Th>
                <Th>Potência (VA)</Th>
                <Th>Subtotal (VA)</Th>
                <Th>Remover</Th>
              </Tr>
            </thead>
            <tbody>
              {elements.map((item, index) => (
                <Tr key={index}>
                  <Td>{item.quantity}</Td>
                  <Td>{item.electricPower}</Td>
                  <Td>{item.quantity * item.electricPower}</Td>
                  <Td style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      aria-label="Remover elemento"
                      title="Remover elemento"
                      onClick={() => setElements(elements.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      <FaMinusCircle />
                    </button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
      <p>
        <strong>Potência total: {totalPower} VA</strong>
      </p>

      <ConductorFields
        idPrefix="circuit"
        title="Avançado: condutores e agrupamento"
        config={config}
        onChange={setConfig}
        conductorOptions={conductorOptions}
        groupingOptions={groupingOptions}
        columnCount={getTerminalColumnCount(conductorOptions)}
        loadedCount={getTerminalColumnCount(conductorOptions)}
      />
    </Modal>
  )
}
